import { doc, increment, updateDoc } from 'firebase/firestore';
import { db } from '../api/firebase/config';
import { CompanyContextType } from '../contexts/auth';
import { GEMINI_MODEL_DEFAULT } from '../constants/enum';
import { getOcrCreditRateWithModel } from '../constants/plans';

/**
 * マスタ照合課金（1結果あたり、ユーザー請求側のみ）
 * Functions 側の MASTER_VERIFICATION_BILLING_JPY と同じ値を保つこと。
 */
const MASTER_VERIFICATION_BILLING_JPY = 15;

/**
 * 部分超過時、ページ順（＝円順）に月間枠（baseRemainingJpy 円）を埋めていき、
 * 累積金額が残り枠を超える結果（タスク）の数を数える。
 *
 * マスタ照合 +15円 は「結果ごと」に課金されるため、超過側にかかる +15円 の数は
 * 「超過ページを含む結果数」で決まる。ページ比例の概算だと跨ぐ結果数がズレ、
 * 確定時に +15円 が過剰／過少消費になる（#2942）。
 *
 * 月間枠の残りは円（baseRemainingJpy）で扱い、1ページ単価未満の端数も枠で使い切る（#2948）。
 *
 * @param resultPageCounts 各結果のページ数（順序＝結果順）。空なら従来の比例見積もりにフォールバック。
 * @param resultCount       生成される結果数。
 * @param baseRemainingJpy  月間枠の残り円（floorしない）。
 * @param creditRate        1ページあたりの単価（円）。
 * @param totalPages        合計ページ数（フォールバックの按分母数）。部分超過分岐で必ず > 0。
 * @returns 超過領域（残り円を超える）に入る結果数。
 */
export const countResultsTouchingOverage = (
  resultPageCounts: number[],
  resultCount: number,
  baseRemainingJpy: number,
  creditRate: number,
  totalPages: number,
): number => {
  // ページ内訳が無い／結果数と整合しない場合は、最低1結果は超過側にかかるとみなしつつ
  // 従来の比例見積もりにフォールバックする（情報不足時の安全側）。
  // 母数は呼び出し元の実 totalPages を使う（resultPageCounts は空でありうるため合計を母数にしない）。
  // 旧経路（再OCR等で resultPageCounts 未指定）でゼロ除算 → NaN になり ocr_credit_balance を
  // 破壊するのを防ぐ（totalPages が万一 0 の場合も 0 除算を回避する）。
  if (resultPageCounts.length === 0 || resultPageCounts.length !== resultCount) {
    if (totalPages <= 0) {
      return 0;
    }
    // 残り円で賄えるページ数（端数切り上げで枠内側を多めに見ない＝floorで超過ページを算出）。
    const baseRemainingPages = creditRate > 0 ? Math.floor(baseRemainingJpy / creditRate) : 0;
    const excessPages = Math.max(0, totalPages - baseRemainingPages);
    return Math.min(resultCount, Math.max(1, Math.round((resultCount * excessPages) / totalPages)));
  }

  // ページ順（＝円順）に枠を埋め、結果の終端までの累積金額が残り円を超えたらその結果は超過側。
  let cumulativeJpy = 0;
  let touchingCount = 0;
  for (const pages of resultPageCounts) {
    cumulativeJpy += pages * creditRate;
    if (cumulativeJpy > baseRemainingJpy) {
      touchingCount += 1;
    }
  }
  return touchingCount;
};

/**
 * FE側OCR仮消費の結果
 */
export interface ProvisionalOcrUsageResult {
  feProvisionalCreditAmount: number;
  totalPages: number;
  // 仮加算（current_month_ocr_cost）に +15円 が実際に乗ったか。
  // hasMasterMapping && enable_master のときだけ true。
  // バックエンドの確定 delta 計算で「仮加算側に +15円 が含まれていたか」の判定に使う。
  provisionalHasMasterMapping: boolean;
  // クレジット仮引きで「超過側」として扱った結果数。
  // 完全超過=全結果数 / 部分超過=超過結果数 / 枠内=0。
  // BE がクレジット消費対象タスク数を逆算する際、+15円込みの仮引き額をページ単価で割ると
  // 超過タスク数を過大評価するため、この値で逆算を置き換える（#2941）。
  feProvisionalExcessResultCount: number;
  // クレジット仮引きで「超過側」として扱ったページ数（=月間枠を超えたページ数）。
  // 完全超過=全ページ / 部分超過=totalPages-baseRemaining / 枠内=0。
  // BE singleResult が超過ページ範囲を +15円込みの仮引き額から逆算すると過大評価するため、
  // この真の超過ページ数で配分の枠内/超過判定を行う（#2942）。
  feProvisionalExcessPages: number;
}

/**
 * FE側でOCR利用枚数・コストの仮加算とクレジット仮引きを実行する共通関数
 *
 * パターンOCR画面・ホーム画面の両方から呼び出される。
 * onSnapshotで監視中のため、仮引き後すぐに残枚数に反映され、二重アップロードを防止する。
 *
 * @param company 企業情報（AuthContextから取得）
 * @param totalPages 合計ページ数
 * @param baseRemainingCount 月間枠の残り枚数（DB上の固定値・floor済み）。後方互換用。
 *                     baseRemainingJpy が渡されない場合のフォールバック（baseRemainingCount × 単価）に使う。
 * @param isProxyMode 代理操作モードかどうか
 * @param geminiModelValue 使用するGeminiモデルのenum値（Pro使用時は単価が変わる）
 * @param hasMasterMapping マスタ照合の対象パターンなら true。+15円 × 結果数 を仮加算する。
 * @param resultCount  生成される結果数 (デフォルト 1)。複数ファイル同時アップロード時に
 *                     結果ごとに +15円 が加算されるよう、ここで結果数を指定する。
 * @param resultPageCounts 各結果のページ数（順序＝結果順）。部分超過時に「ページ順に枠を埋めて
 *                     超過側に入る結果数」を正確に数えるために使う。空配列なら従来の比例見積もりにフォールバック。
 * @param baseRemainingJpy 月間枠の残り円（floorしない / #2948）。端数も枠で使い切る。
 *                     null の場合は baseRemainingCount × 単価 にフォールバック（後方互換）。
 * @returns 仮引きしたクレジット額と合計ページ数
 */
export const executeProvisionalOcrUsage = async (
  company: CompanyContextType,
  totalPages: number,
  baseRemainingCount: number | null,
  isProxyMode: boolean,
  geminiModelValue: number = GEMINI_MODEL_DEFAULT,
  hasMasterMapping = false,
  resultCount = 1,
  resultPageCounts: number[] = [],
  baseRemainingJpy: number | null = null,
): Promise<ProvisionalOcrUsageResult> => {
  let feProvisionalCreditAmount = 0;
  // クレジット仮引きで「超過側」として扱った結果数（BE へ伝播）。
  let feProvisionalExcessResultCount = 0;
  // クレジット仮引きで「超過側」として扱ったページ数（BE へ伝播）。
  let feProvisionalExcessPages = 0;
  // 仮加算に +15円 が乗るか（hasMasterMapping && enable_master）。
  // 代理モードでは current_month_ocr_cost を加算しないため常に false。
  const provisionalHasMasterMapping = !isProxyMode && hasMasterMapping && company.enable_master;
  const companyDocRef = doc(db, 'companies', company.company_id);

  if (!isProxyMode) {
    const creditRate = getOcrCreditRateWithModel(company.plan, geminiModelValue);
    // OCR本体の仮加算コストに、マスタ照合課金 (物理ページごと +15円 / #2953) を上乗せ。
    // 企業のマスタ照合が無効（enable_master=false）なら照合は走らないので加算しない
    // （バックエンドの実課金 enableMaster && hasMasterMapping と整合させる）。
    // 単位がページなので、pagePerResult（1結果=1ページ）も singleResult（1結果=複数ページ）も
    // 一律「totalPages × +15円」で計算できる（#2942 の結果単位カウントは不要になった）。
    const masterVerificationBillingJpy = provisionalHasMasterMapping
      ? MASTER_VERIFICATION_BILLING_JPY * totalPages
      : 0;
    const bodyCost = totalPages * creditRate;
    const provisionalCost = bodyCost + masterVerificationBillingJpy;

    // 月間枠の残りは円で扱い、1ページ単価未満の端数も枠で使い切る（#2948）。
    // baseRemainingJpy 未指定時は従来どおり baseRemainingCount × 単価 にフォールバック（後方互換）。
    const remainingJpy =
      baseRemainingJpy !== null
        ? Math.max(0, baseRemainingJpy)
        : Math.max(0, (baseRemainingCount ?? 0) * creditRate);
    // 月間枠を超える OCR 本体額（マスタ +15円 は含まない）。
    const excessBodyJpy = Math.max(0, bodyCost - remainingJpy);

    if (remainingJpy <= 0 && company.ocr_credit_balance > 0) {
      // 月間枠超過済み: 全額がクレジット対象（全ページが超過側）。+15円 は全ページ分。
      feProvisionalCreditAmount = provisionalCost;
      feProvisionalExcessResultCount = provisionalHasMasterMapping ? resultCount : 0;
      feProvisionalExcessPages = totalPages;
      await updateDoc(companyDocRef, {
        current_month_ocr_count: increment(totalPages),
        current_month_ocr_cost: increment(provisionalCost),
        ocr_credit_balance: increment(-provisionalCost),
      });
    } else if (remainingJpy > 0 && excessBodyJpy > 0 && company.ocr_credit_balance > 0) {
      // 一部が月間枠内、残りが超過（端数も枠で使い切ったうえでの超過分のみクレジット / #2948）
      // 超過ページ数は端数を超過側へ繰り上げ（ceil）。BE 配分の境界に渡す。
      const excessPages = Math.ceil(excessBodyJpy / creditRate);
      // マスタ照合 +15円 は物理ページごと（#2953）。超過側にかかる +15円 は「超過ページ数 × 15円」。
      // ページ単位課金なので #2942 の結果単位カウントは不要（超過ページ数だけで決まる）。
      const excessMasterVerificationBillingJpy = provisionalHasMasterMapping
        ? MASTER_VERIFICATION_BILLING_JPY * excessPages
        : 0;
      // 超過クレジット消費 = 超過本体額（端数差引済み）+ 超過ページ分の +15円
      const excessCost = excessBodyJpy + excessMasterVerificationBillingJpy;
      feProvisionalCreditAmount = excessCost;
      // BE 互換のため超過結果数も渡すが、ページ単位課金では BE は超過ページ数を主に使う。
      feProvisionalExcessResultCount = provisionalHasMasterMapping
        ? countResultsTouchingOverage(
            resultPageCounts,
            resultCount,
            remainingJpy,
            creditRate,
            totalPages,
          )
        : 0;
      feProvisionalExcessPages = excessPages;
      await updateDoc(companyDocRef, {
        current_month_ocr_count: increment(totalPages),
        current_month_ocr_cost: increment(provisionalCost),
        ocr_credit_balance: increment(-excessCost),
      });
    } else {
      // 月間枠内: クレジット消費なし
      await updateDoc(companyDocRef, {
        current_month_ocr_count: increment(totalPages),
        current_month_ocr_cost: increment(provisionalCost),
      });
    }
  } else {
    // 代理モード: proxy_operation_countのみインクリメント
    await updateDoc(companyDocRef, {
      proxy_operation_count: increment(totalPages),
    });
  }

  return {
    feProvisionalCreditAmount,
    totalPages,
    provisionalHasMasterMapping,
    feProvisionalExcessResultCount,
    feProvisionalExcessPages,
  };
};
